/**
 * productNameUtils — Helpers pour nettoyer/normaliser les noms de produits
 *
 * Plusieurs partenaires (notamment iQueens) renvoient des noms de produits
 * avec la taille en suffixe : "Adidas T-Shirt Originals Femme XL", "Nike
 * Air Max Femme 38". Ça crée des doublons "fantômes" partout dans l'app
 * (dédup par nom+marque+prix voit 2 produits distincts pour le même article
 * en 2 tailles).
 *
 * Cette logique est centralisée ici pour qu'elle soit appliquée :
 *   - À la source : quand le partenaire crée le produit (IQueensService.js)
 *   - À la dédup  : pour normaliser même si la source n'a pas été nettoyée
 */

// Suffixes de taille couramment trouvés en fin de nom :
//   - lettres : XS, S, M, L, XL, XXL, XXXL, 3XL, 4XL, etc.
//   - chiffres : tailles vêtements (32-58), pointures (33-50)
//   - taille unique : "Unique", "U", "TU", "Taille unique"
const SIZE_SUFFIX_RE = /\s+(?:taille\s+)?(?:XX?X?S|XX?X?L|[2-5]XL|S|M|L|XS|XL|TU|U|Unique|Taille\s+unique|\d{2}(?:[\/\-]\d{2})?)$/i;

// Quelques mots qui peuvent ressembler à une taille mais ne le sont pas dans
// notre contexte (à protéger contre faux positifs si jamais on étend la regex).
// Ex: "Femme" en fin de nom NE doit PAS être retiré (c'est le genre, pas une taille).
// La regex ci-dessus ne matche pas "Femme" donc on est tranquille.

/**
 * Retire la taille en fin de nom de produit, si présente.
 * Idempotent (safe à appliquer plusieurs fois).
 *
 * Examples :
 *   stripTrailingSize("Adidas T-Shirt Originals Femme XL")     → "Adidas T-Shirt Originals Femme"
 *   stripTrailingSize("Nike Air Max Femme 38")                 → "Nike Air Max Femme"
 *   stripTrailingSize("Calvin Klein Robe Mini Femme S")        → "Calvin Klein Robe Mini Femme"
 *   stripTrailingSize("Adidas Stan Smith Femme")               → "Adidas Stan Smith Femme" (inchangé)
 *   stripTrailingSize("Nike Air Max")                          → "Nike Air Max"
 */
export function stripTrailingSize(name) {
  if (typeof name !== 'string' || !name) return name || '';
  let cleaned = name.trim();
  // On peut avoir plusieurs suffixes accolés en fin (rare mais possible) → boucle limitée
  let prev;
  let safety = 3;
  do {
    prev = cleaned;
    cleaned = cleaned.replace(SIZE_SUFFIX_RE, '').trim();
    safety -= 1;
  } while (cleaned !== prev && safety > 0);
  // Sécurité : ne jamais retourner vide. Si la regex a tout mangé (cas impossible
  // en pratique sur de vrais noms), on garde la version originale.
  return cleaned.length >= 3 ? cleaned : name.trim();
}

/**
 * Extrait la taille en suffixe d'un nom de produit, ou retourne null si absente.
 *
 * Examples :
 *   extractTrailingSize("Adidas T-Shirt Femme XL") → "XL"
 *   extractTrailingSize("Nike Air Max Femme 38")    → "38"
 *   extractTrailingSize("Levi 501 Jean Femme 32/34")→ "32/34"
 *   extractTrailingSize("Vans Old Skool Femme")     → null
 */
export function extractTrailingSize(name) {
  if (typeof name !== 'string' || !name) return null;
  const cleaned = name.trim();
  // Réutilise la même regex mais on capture
  const CAPTURE_RE = /\s+(?:taille\s+)?(XX?X?S|XX?X?L|[2-5]XL|S|M|L|XS|XL|TU|U|Unique|Taille\s+unique|\d{2}(?:[\/\-]\d{2})?)$/i;
  const m = CAPTURE_RE.exec(cleaned);
  if (!m) return null;
  // Normalisation : majuscule pour les lettres, telle quelle pour les chiffres
  const raw = m[1];
  // "Taille unique" / "Unique" / "TU" / "U" → "Unique"
  if (/^(taille\s+unique|unique|tu|u)$/i.test(raw)) return 'Unique';
  // Sinon majuscule
  return raw.toUpperCase();
}

/**
 * Nettoie un nom de produit pour usage d'affichage et de dédup.
 *  - retire la taille en suffixe
 *  - trim + collapse espaces multiples
 *  - garde la casse d'origine (pour ne pas casser les noms de marques)
 */
export function cleanProductName(name) {
  if (typeof name !== 'string') return '';
  return stripTrailingSize(name).replace(/\s+/g, ' ').trim();
}

/**
 * Clé de normalisation pour la dédup (lowercase + sans taille).
 * Utiliser cette clé permet de regrouper "Adidas T-Shirt Femme XL" et
 * "Adidas T-Shirt Femme S" comme un seul produit.
 */
export function normalizedNameKey(name) {
  return cleanProductName(name).toLowerCase();
}
